import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Button, DatePicker, Descriptions, Form, Input, Modal, Select, Space, Tabs, Tag, Typography } from 'antd';
import { CheckCircleOutlined, EyeOutlined, ReloadOutlined, SyncOutlined } from '@ant-design/icons';
import moment from 'moment';
import { isMobile } from 'react-device-detect';
import { NotificationManager } from 'react-notifications';
import PageTitle from '@/components/PageTitle';
import TableActionBar from '@/components/TableActionBar';
import DynamicTable from '@/components/DynamicTable';
import BaseSelect from '@/components/Elements/BaseSelect';
import { getPagination } from '@/utils/helpers';
import { checkPermission } from '@/utils/permission';
import {
    fetchCommissionHolds,
    fetchCommissionProcessingJobs,
    recalculateCommission,
    recordCommissionCareOutcome,
    retryCommissionProcessingJob,
} from '@/redux/actions/commissionActions';
import { money } from './commissionLifecycleHelpers';
import CommissionIneligibleServicesTab from './CommissionIneligibleServicesTab';
import CommissionRelatedEmployees from './CommissionRelatedEmployees';
import { CommissionCustomerColumn, CommissionServiceColumn } from './CommissionCustomerServiceColumns';

const { Paragraph, Text } = Typography;
const { RangePicker } = DatePicker;
const holdLabels = { REV: 'Doanh thu chưa đủ điều kiện', ROUTING: 'Chưa xác định được người hưởng', ALLOC: 'Doanh thu của người hưởng đang giữ', COMM: 'Hoa hồng đang giữ' };
const statusViews = {
    PENDING: { label: 'Chờ xử lý', color: 'blue' },
    RUNNING: { label: 'Đang xử lý', color: 'processing' },
    WAITING: { label: 'Chờ bổ sung thông tin', color: 'orange' },
    FAILED_RETRY: { label: 'Cần xử lý lại', color: 'volcano' },
};
const reasonOptions = [
    ['WAITING_CONDITION', 'Chờ điều kiện'],
    ['MISSING_POLICY', 'Thiếu chính sách'],
    ['POLICY_RULE_CONFLICT', 'Chính sách hoa hồng chưa phù hợp'],
    ['MISSING_BENEFICIARY', 'Thiếu người hưởng / phân bổ'],
    ['PERIOD_WORKFLOW_BLOCKED', 'Kỳ đã chốt hoặc khoản đã qua bước xử lý'],
    ['BUSINESS_WAITING', 'Chờ xử lý nghiệp vụ'],
    ['SYSTEM_RETRYABLE', 'Cần thử xử lý lại'],
].map(([value, label]) => ({ value, label }));

const dateTime = value => value ? moment(value).format('DD/MM/YYYY HH:mm') : '—';

export default function CommissionHoldPage({ embedded = false }) {
    const dispatch = useDispatch();
    const config = useSelector(state => state.config || {});
    const [jobs, setJobs] = useState({ data: [], total: 0, current_page: 1, per_page: 30 });
    const [holds, setHolds] = useState({ data: [], total: 0, current_page: 1, per_page: 30 });
    const [ineligibleTotal, setIneligibleTotal] = useState(0);
    const [loadingJobs, setLoadingJobs] = useState(false);
    const [loadingHolds, setLoadingHolds] = useState(false);
    const [processingIds, setProcessingIds] = useState([]);
    const [selectedJobIds, setSelectedJobIds] = useState([]);
    const [detail, setDetail] = useState(null);
    const [selectedHold, setSelectedHold] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [filterForm] = Form.useForm();
    const [holdFilterForm] = Form.useForm();
    const [careForm] = Form.useForm();
    const allowViewJobs = checkPermission('commission_result', 'view_trace');
    const allowCalculate = checkPermission('commission_result', 'calculate');
    const allowCareOutcome = checkPermission('commission_hold', 'care_outcome');

    const buildFilters = useCallback((page = 1, perPage = 30) => {
        const values = filterForm.getFieldsValue();
        return {
            keyword: values.keyword,
            status: values.status,
            reason_category: values.reason_category,
            source_type: values.source_type,
            employee_id: values.employee_id,
            from_date: values.occurred_at?.[0]?.format('YYYY-MM-DD'),
            to_date: values.occurred_at?.[1]?.format('YYYY-MM-DD'),
            page,
            per_page: perPage,
        };
    }, [filterForm]);

    const loadJobs = useCallback(async (page = 1, perPage = 30) => {
        if (!allowViewJobs) return;
        setLoadingJobs(true);
        try {
            const result = await dispatch(fetchCommissionProcessingJobs(buildFilters(page, perPage)));
            setJobs(result);
            setSelectedJobIds([]);
        } catch (error) { /* Action đã hiển thị lỗi. */ }
        finally { setLoadingJobs(false); }
    }, [allowViewJobs, buildFilters, dispatch]);

    const loadHolds = useCallback(async (page = 1, perPage = 30) => {
        setLoadingHolds(true);
        try {
            const values = holdFilterForm.getFieldsValue();
            setHolds(await dispatch(fetchCommissionHolds({
                keyword: values.keyword,
                hold_type: values.hold_type,
                employee_id: values.employee_id,
                page,
                per_page: perPage,
            })));
        }
        catch (error) { /* Action đã hiển thị lỗi. */ }
        finally { setLoadingHolds(false); }
    }, [dispatch, holdFilterForm]);

    useEffect(() => { if (allowViewJobs) loadJobs(); loadHolds(); }, [allowViewJobs, loadHolds, loadJobs]);

    const retryOne = async job => {
        setProcessingIds(ids => [...ids, job.job_id]);
        try {
            const result = await dispatch(retryCommissionProcessingJob(job.job_id, { notify: false }));
            if (['COMPLETED', 'SKIPPED'].includes(result.status)) NotificationManager.success(result.message || 'Đã xử lý hoa hồng thành công.');
            else NotificationManager.warning(result.reason || result.message || 'Khoản hoa hồng vẫn cần xử lý thêm.');
            await Promise.all([
                loadJobs(jobs.current_page, jobs.per_page),
                loadHolds(holds.current_page, holds.per_page),
            ]);
        } finally { setProcessingIds(ids => ids.filter(id => id !== job.job_id)); }
    };

    const retrySelected = async () => {
        const selected = jobs.data.filter(job => selectedJobIds.includes(job.job_id) && job.can_retry);
        if (!selected.length) return;
        setProcessingIds(selected.map(job => job.job_id));
        let completed = 0;
        let waiting = 0;
        for (const job of selected) {
            try {
                const result = await dispatch(retryCommissionProcessingJob(job.job_id, { notify: false }));
                if (['COMPLETED', 'SKIPPED'].includes(result.status)) completed += 1;
                else waiting += 1;
            } catch (error) { waiting += 1; }
        }
        setProcessingIds([]);
        NotificationManager.info(`Đã xử lý ${selected.length} khoản: ${completed} thành công, ${waiting} còn chờ xử lý.`);
        await Promise.all([
            loadJobs(jobs.current_page, jobs.per_page),
            loadHolds(holds.current_page, holds.per_page),
        ]);
    };

    const recalculateHold = async hold => {
        if (!hold.customer_service_id) return;
        const processingKey = `hold-${hold.hold_type}-${hold.id}`;
        setProcessingIds(ids => [...ids, processingKey]);
        try {
            await dispatch(recalculateCommission(hold.customer_service_id));
            await Promise.all([
                loadJobs(jobs.current_page, jobs.per_page),
                loadHolds(holds.current_page, holds.per_page),
            ]);
        } finally { setProcessingIds(ids => ids.filter(id => id !== processingKey)); }
    };

    const submitCareOutcome = async () => {
        const values = await careForm.validateFields();
        setSubmitting(true);
        try {
            await dispatch(recordCommissionCareOutcome({
                hold_type: selectedHold.hold_type,
                hold_id: selectedHold.id,
                outcome: values.outcome,
                contacted_at: values.contacted_at.format('YYYY-MM-DD HH:mm:ss'),
                note: values.note,
            }));
            setSelectedHold(null);
            careForm.resetFields();
            await loadHolds(holds.current_page, holds.per_page);
        } finally { setSubmitting(false); }
    };

    const jobColumns = [
        { title: 'Khách hàng', key: 'customer', width: 240, render: (_, row) => <CommissionCustomerColumn record={row} /> },
        { title: 'Dịch vụ', key: 'service_name', width: 230, render: (_, row) => <CommissionServiceColumn customerServiceId={row.customer_service_id} serviceName={row.service_name} /> },
        { title: 'Phiếu thu / Khoản tiền', key: 'source', width: 190, render: (_, row) => <><div>{row.source_label}</div><Text type="secondary">Lần ghi nhận {row.source_revision}</Text></> },
        { title: 'Ngày phát sinh', dataIndex: 'business_event_at', key: 'business_event_at', width: 150, render: dateTime },
        { title: 'Nhân viên liên quan', dataIndex: 'related_employees', key: 'related_employees', width: 260, render: value => <CommissionRelatedEmployees employees={value} /> },
        { title: 'Số tiền nguồn', dataIndex: 'source_amount', key: 'source_amount', align: 'right', width: 145, render: value => value === null || value === undefined ? '—' : money(value) },
        { title: 'Phân loại', dataIndex: 'reason_label', key: 'reason_label', width: 190, render: value => <Tag color="orange">{value}</Tag> },
        { title: 'Trạng thái', dataIndex: 'status', key: 'status', width: 140, render: value => { const view = statusViews[value] || { label: value, color: 'default' }; return <Tag color={view.color}>{view.label}</Tag>; } },
        { title: 'Công đoạn chưa hoàn tất', dataIndex: 'failure_step_label', key: 'failure_step_label', width: 210, render: (value, row) => value || (row.failure_step ? 'Xử lý hoa hồng' : '—') }, // [PROCESSING-JOB-USER-LABEL]
        { title: 'Thử gần nhất', dataIndex: 'last_attempt_at', key: 'last_attempt_at', width: 160, render: dateTime },
        { title: 'Số lần', dataIndex: 'attempt_count', key: 'attempt_count', align: 'center', width: 80 },
        { title: 'Thao tác', key: 'action', fixed: 'right', width: 160, render: (_, row) => <Space><Button size="small" icon={<EyeOutlined />} onClick={() => setDetail(row)}>Nguyên nhân</Button>{allowCalculate && row.can_retry && <Button size="small" type="primary" icon={<SyncOutlined />} loading={processingIds.includes(row.job_id)} onClick={() => retryOne(row)}>Tính lại</Button>}</Space> },
    ];

    const holdColumns = [
        { title: 'Loại chờ xử lý', dataIndex: 'hold_type', key: 'hold_type', width: 220, render: value => <Tag color="orange">{holdLabels[value] || value}</Tag> },
        { title: 'Khách hàng', key: 'customer', width: 240, render: (_, row) => <CommissionCustomerColumn record={row} /> },
        { title: 'Tên dịch vụ', key: 'service_name', width: 240, render: (_, row) => <CommissionServiceColumn customerServiceId={row.customer_service_id} serviceName={row.service_name || (row.customer_service_id ? `Dịch vụ #${row.customer_service_id}` : null)} /> },
        { title: 'Nhân viên liên quan', dataIndex: 'related_employees', key: 'related_employees', width: 260, render: value => <CommissionRelatedEmployees employees={value} /> },
        { title: 'Nội dung cần xử lý', dataIndex: 'reason', key: 'reason', width: 340 },
        { title: 'Ngày giữ', dataIndex: 'held_at', key: 'held_at', width: 155, render: dateTime },
        { title: 'Ban đầu', dataIndex: 'amount', key: 'amount', align: 'right', width: 140, render: money },
        { title: 'Còn chờ', dataIndex: 'remaining_amount', key: 'remaining_amount', align: 'right', width: 140, render: value => <Text strong>{money(value)}</Text> },
        { title: 'Hoa hồng đang giữ', dataIndex: 'held_commission_amount', key: 'held_commission_amount', align: 'right', width: 170, render: value => value !== null && value !== undefined ? <Text strong>{money(value)}</Text> : '—' },
        { title: 'Thao tác', key: 'action', width: 160, fixed: 'right', render: (_, row) => <Space><Button size="small" icon={<EyeOutlined />} onClick={() => setDetail(row)}>Nguyên nhân</Button>{allowCalculate && row.can_recalculate && <Button size="small" type="primary" icon={<SyncOutlined />} loading={processingIds.includes(`hold-${row.hold_type}-${row.id}`)} onClick={() => recalculateHold(row)}>Tính lại</Button>}{['ALLOC', 'COMM'].includes(row.hold_type) && allowCareOutcome && <Button size="small" icon={<CheckCircleOutlined />} onClick={() => setSelectedHold(row)}>Ghi nhận CSKH</Button>}</Space> },
    ];

    return <div>
        {!embedded && <PageTitle routes={[{ path: '/', breadcrumbName: 'Trang chủ' }, { path: '#', breadcrumbName: 'Khoản hoa hồng chờ xử lý' }]} title="Khoản hoa hồng chờ xử lý" />}
        <Tabs defaultActiveKey={allowViewJobs ? 'processing' : 'holds'}>
            {allowViewJobs && (
                <Tabs.TabPane
                    tab={`Khoản tiền cần xử lý (${jobs.total || 0})`}
                    key="processing"
                >
                    <Alert
                        type="info"
                        message={<>
                            <div>Đây là các khoản tiền hoặc thay đổi dịch vụ đã được ghi nhận, nhưng kết quả hoa hồng liên quan chưa cập nhật xong.</div>
                            <div><strong>Ví dụ:</strong> phiếu thu được thêm hoặc sửa; số dư hay tiền cọc được dùng cho dịch vụ; dịch vụ được chuyển đổi; trạng thái hoặc nhân sự của dịch vụ thay đổi; hoặc lần xử lý trước bị gián đoạn.</div>
                            <div>Hãy xem Nguyên nhân và hướng dẫn của từng khoản. Hoàn thiện thông tin được yêu cầu, hoặc xử lý phần chênh lệch ở kỳ đang mở nếu kết quả cũ đã chốt, duyệt hoặc đưa vào chi trả, rồi chọn Tính lại. Khoản sẽ tự rời danh sách khi đã xử lý xong hoặc không phát sinh hoa hồng.</div>
                        </>}
                        style={{ marginBottom: 12 }}
                    />
                    <Form
                        form={filterForm}
                        component={false}
                    >
                        <TableActionBar
                            isShowAddButton={false}
                            isShowDeleteButton={false}
                            showSearch={false}
                            showFilter={false}
                        >
                            <Form.Item name="keyword" noStyle>
                                <Input
                                    allowClear
                                    placeholder="Khách hàng, dịch vụ, phiếu thu"
                                    className="table-button"
                                    style={{ width: 230 }}
                                />
                            </Form.Item>

                            <Form.Item name="occurred_at" noStyle>
                                <RangePicker
                                    format="DD/MM/YYYY"
                                    className="table-button"
                                />
                            </Form.Item>

                            <Form.Item name="status" noStyle>
                                <Select
                                    allowClear
                                    placeholder="Trạng thái"
                                    className="table-button"
                                    style={{ width: 155 }}
                                    options={Object.entries(statusViews).map(([value, view]) => ({
                                        value,
                                        label: view.label,
                                    }))}
                                />
                            </Form.Item>

                            <Form.Item name="reason_category" noStyle>
                                <Select
                                    allowClear
                                    placeholder="Nhóm nguyên nhân"
                                    className="table-button"
                                    style={{ width: 230 }}
                                    options={reasonOptions}
                                />
                            </Form.Item>

                            <Form.Item name="source_type" noStyle>
                                <Select
                                    allowClear
                                    placeholder="Loại nguồn"
                                    className="table-button"
                                    style={{ width: 170 }}
                                    options={[
                                        { value: 'RECEIPT', label: 'Phiếu thu' },
                                        { value: 'BALANCE_APPLICATION', label: 'Số dư khách hàng' },
                                        { value: 'DEPOSIT_ALLOCATION', label: 'Tiền cọc' },
                                        { value: 'SERVICE_CONVERSION', label: 'Chuyển đổi dịch vụ' },
                                    ]}
                                />
                            </Form.Item>

                            <Form.Item name="employee_id" noStyle>
                                <BaseSelect options={config.users || []} optionValue="user_id" optionLabel="display_name" showSearch placeholder="Nhân viên liên quan" className="table-button" style={{ width: 240 }} />
                            </Form.Item>

                            <Button
                                type="primary"
                                className="table-button"
                                onClick={() => loadJobs(1, jobs.per_page)}
                            >
                                Lọc
                            </Button>

                            <Button
                                className="table-button"
                                onClick={() => {
                                    filterForm.resetFields();
                                    setTimeout(() => loadJobs(1, jobs.per_page), 0);
                                }}
                            >
                                Xóa lọc
                            </Button>

                            {allowCalculate && (
                                <Button
                                    className="table-button"
                                    type="primary"
                                    icon={<SyncOutlined />}
                                    disabled={!selectedJobIds.length}
                                    loading={processingIds.length > 0}
                                    onClick={retrySelected}
                                >
                                    Tính lại đã chọn
                                </Button>
                            )}

                            <Button
                                className="table-button"
                                icon={<ReloadOutlined />}
                                loading={loadingJobs}
                                onClick={() => loadJobs(jobs.current_page, jobs.per_page)}
                            >
                                Làm mới
                            </Button>
                        </TableActionBar>
                    </Form>

                    <DynamicTable
                        rowKey="job_id"
                        moduleKey="commission_processing_jobs"
                        isLoading={loadingJobs}
                        dataSource={jobs.data || []}
                        columns={jobColumns}
                        scroll={{ x: 2050 }}
                        rowSelection={
                            allowCalculate
                                ? {
                                    selectedRowKeys: selectedJobIds,
                                    onChange: setSelectedJobIds,
                                    getCheckboxProps: row => ({
                                        disabled:
                                            !row.can_retry ||
                                            processingIds.includes(row.job_id),
                                    }),
                                }
                                : undefined
                        }
                        rowClassName={row => row.can_retry ? '' : 'disabled-row'}
                        pagination={getPagination(jobs)}
                        onChange={pagination => loadJobs(pagination.current, pagination.pageSize)}
                    />
                </Tabs.TabPane>
            )}
            <Tabs.TabPane forceRender tab={`Dịch vụ chưa đủ điều kiện (${ineligibleTotal})`} key="ineligible-services">
                <CommissionIneligibleServicesTab onTotalChange={setIneligibleTotal} />
            </Tabs.TabPane>
            <Tabs.TabPane tab={`Khoản đang giữ (${holds.total || 0})`} key="holds">
                <Alert
                    type="info"
                    message="Tab này hiển thị phần doanh thu hoặc hoa hồng còn đang được giữ vì chưa đủ điều kiện phân bổ, chưa xác định được người hưởng hoặc đang chờ điều kiện mở giữ theo chính sách. Hãy xem Nội dung cần xử lý: bổ sung dữ liệu rồi Tính lại với khoản cho phép, hoặc Ghi nhận CSKH khi được yêu cầu; hệ thống chỉ mở khoản giữ khi các điều kiện tương ứng đã được đáp ứng."
                    style={{ marginBottom: 12 }}
                />
                <Form form={holdFilterForm} component={false}>
                    <TableActionBar isShowAddButton={false} isShowDeleteButton={false} showSearch={false} showFilter={false}>
                        <Form.Item name="keyword" noStyle><Input allowClear placeholder="Khách hàng, dịch vụ, nhân viên" className="table-button" style={{ width: 240 }} /></Form.Item>
                        <Form.Item name="hold_type" noStyle><Select allowClear placeholder="Loại khoản đang giữ" className="table-button" style={{ width: 230 }} options={Object.entries(holdLabels).map(([value, label]) => ({ value, label }))} /></Form.Item>
                        <Form.Item name="employee_id" noStyle><BaseSelect options={config.users || []} optionValue="user_id" optionLabel="display_name" showSearch placeholder="Nhân viên liên quan" className="table-button" style={{ width: 240 }} /></Form.Item>
                        <Button type="primary" className="table-button" onClick={() => loadHolds(1, holds.per_page)}>Lọc</Button>
                        <Button className="table-button" onClick={() => { holdFilterForm.resetFields(); setTimeout(() => loadHolds(1, holds.per_page), 0); }}>Xóa lọc</Button>
                        <Button className="table-button" icon={<ReloadOutlined />} loading={loadingHolds} onClick={() => loadHolds(holds.current_page, holds.per_page)}>Làm mới</Button>
                    </TableActionBar>
                </Form>
                <div className='mt-2'>
                    <DynamicTable
                        rowKey={row => `${row.hold_type}-${row.id}`}
                        rowSelection={false}
                        moduleKey="commission_holds"
                        isLoading={loadingHolds}
                        dataSource={holds.data || []}
                        columns={holdColumns}
                        scroll={{ x: "max-content" }}
                        pagination={getPagination(holds)}
                        onChange={pagination => loadHolds(pagination.current, pagination.pageSize)}
                    />
                </div>
            </Tabs.TabPane>
        </Tabs>

        <Modal visible={!!detail} footer={null} width={isMobile ? '100%' : '62%'} maskClosable={false} title="Nguyên nhân khoản chờ xử lý" onCancel={() => setDetail(null)}>
            {detail && <><Descriptions bordered column={isMobile ? 1 : 2} size="small"><Descriptions.Item label="Khách hàng">{detail.customer_name || 'Chưa xác định'}{detail.customer_code ? ` (${detail.customer_code})` : ''}</Descriptions.Item><Descriptions.Item label="Dịch vụ">{detail.service_name || 'Chưa xác định'}</Descriptions.Item><Descriptions.Item label="Khoản phát sinh">{detail.source_label || holdLabels[detail.hold_type] || '—'}</Descriptions.Item><Descriptions.Item label="Số tiền">{detail.source_amount === null || detail.source_amount === undefined ? '—' : money(detail.source_amount)}</Descriptions.Item><Descriptions.Item label="Phân loại">{detail.reason_label || 'Chưa phân loại'}</Descriptions.Item><Descriptions.Item label="Công đoạn chưa hoàn tất">{detail.failure_step_label || (detail.failure_step ? 'Xử lý hoa hồng' : '—')}</Descriptions.Item><Descriptions.Item label="Ngày phát sinh">{dateTime(detail.business_event_at || detail.occurred_at)}</Descriptions.Item><Descriptions.Item label="Lần thử gần nhất">{dateTime(detail.last_attempt_at)}</Descriptions.Item><Descriptions.Item label="Số lần thử">{detail.attempt_count ?? '—'}</Descriptions.Item><Descriptions.Item label="Nhân viên liên quan"><CommissionRelatedEmployees employees={detail.related_employees} /></Descriptions.Item></Descriptions><Text strong>Nguyên nhân</Text><Paragraph style={{ marginTop: 8 }}>{detail.reason || 'Chưa có thông tin nguyên nhân chi tiết.'}</Paragraph><Text strong>Hướng xử lý</Text><Paragraph style={{ marginTop: 8 }}>{detail.suggested_action || 'Kiểm tra dữ liệu nghiệp vụ liên quan rồi thử lại.'}</Paragraph></>}
        </Modal>

        <Modal visible={!!selectedHold} width={isMobile ? '100%' : '52%'} maskClosable={false} title="Kết quả CSKH sau thời gian giữ" okText="Ghi nhận" cancelText="Hủy" confirmLoading={submitting} onOk={submitCareOutcome} onCancel={() => { setSelectedHold(null); careForm.resetFields(); }}>
            <Form form={careForm} layout="vertical"><Form.Item name="contacted_at" label="Thời điểm CSKH liên hệ khách" extra="Hệ thống đối chiếu thời điểm này với thời hạn giữ tính từ lúc doanh thu phát sinh." rules={[{ required: true, message: 'Chọn thời điểm đã liên hệ khách.' }]}><DatePicker showTime style={{ width: '100%' }} /></Form.Item><Form.Item name="outcome" label="Kết quả liên hệ" extra="Chỉ kết quả khách không phản hồi vấn đề/khiếu nại mới có thể mở khoản giữ khi đã đủ thời gian." rules={[{ required: true, message: 'Chọn kết quả liên hệ.' }]}><Select options={[{ value: 'NO_ISSUE', label: 'Khách không phản hồi vấn đề/khiếu nại' }, { value: 'NO_RESPONSE', label: 'Không liên hệ được khách' }, { value: 'HAS_ISSUE', label: 'Khách có vấn đề hoặc khiếu nại' }]} /></Form.Item><Form.Item name="note" label="Ghi chú CSKH"><Input.TextArea rows={3} maxLength={1000} /></Form.Item></Form>
        </Modal>
    </div>;
}
